import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getRedirectResult, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import AdminGuard from './components/AdminGuard';
import Sidebar from './components/Sidebar';
import AuditToast from './components/AuditToast';
import NotificationBell from './components/NotificationBell';
import Overview from './pages/Overview';
import MerchantList from './pages/Merchants/MerchantList';
import MerchantDetail from './pages/Merchants/MerchantDetail';
import PromoCodes from './pages/PromoCodes';
import Operations from './pages/Operations';
import TaskQueue from './pages/TaskQueue';
import AuditLog from './pages/AuditLog';
import Analytics from './pages/Analytics';
import AdminUsers from './pages/AdminUsers';
import GSTMaster from './pages/GSTMaster';
import Unauthorized from './pages/Unauthorized';
import Webhooks from './pages/integrations/Webhooks';
import WhatsAppBot from './pages/integrations/WhatsAppBot';
import ApiKeys from './pages/integrations/ApiKeys';
import { fetchFeatureFlags } from './services/firestore';
import useAdminRole from './hooks/useAdminRole';

function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) navigate('/', { replace: true });
    });

    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) navigate('/', { replace: true });
      } catch (err) {
        setError(err?.message || 'Sign-in failed. Please try again.');
      }
    })();

    return unsubscribe;
  }, [navigate]);

  const startGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result?.user) navigate('/', { replace: true });
    } catch (err) {
      const code = err?.code || '';
      const shouldFallback =
        code === 'auth/popup-blocked' ||
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/operation-not-supported-in-this-environment';

      if (shouldFallback) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }

      setError(err?.message || 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wb-login-wrap">
      <h1>WorkBill Admin Dashboard</h1>
      <p>Google Sign-In only. Access is restricted to users in /admins.</p>
      <button
        type="button"
        disabled={loading}
        onClick={startGoogleSignIn}
      >
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </button>
      {error ? <p style={{ color: '#c62828', marginTop: 12 }}>{error}</p> : null}
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const { role } = useAdminRole();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const flags = await fetchFeatureFlags();
      if (!alive) return;
      setMaintenanceMode(Boolean(flags.maintenanceMode));
    };
    load();
    const timer = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const titles = {
    '/': ['Overview', 'Live operational snapshot'],
    '/merchants': ['Merchants', 'Search and manage merchant accounts'],
    '/promo-codes': ['Promo Codes', 'Create and control subscription discounts'],
    '/operations': ['Operations', 'Maintenance controls, flags, and SMS broadcast queue'],
    '/operations/feature-flags': ['Feature Flags', 'Firestore-backed rollout controls'],
    '/operations/maintenance': ['Maintenance', 'Merchant-facing maintenance state and message'],
    '/operations/task-queue': ['Task Queue', 'Approval requests, failed jobs, and operational tasks'],
    '/operations/broadcasts': ['Broadcasts', 'SMS and WhatsApp audience jobs'],
    '/integrations': ['Integrations', 'Platform webhooks, bot health, and provider credentials'],
    '/integrations/webhooks': ['Webhooks', 'Central platform webhook configuration and delivery log'],
    '/integrations/whatsapp-bot': ['WhatsApp Bot', 'Server health, registered businesses, and recent bills'],
    '/integrations/api-keys': ['API Keys', 'Read-only platform integration audit view'],
    '/gst-master': ['GST Master', 'Version tag and cache invalidation controls'],
    '/audit-log': ['Audit Log', 'Immutable record of admin actions'],
    '/analytics': ['Analytics', 'Business health metrics'],
    '/admin-users': ['Admin Users', 'Access control for dashboard admins'],
  };

  const routeKey = Object.keys(titles)
    .sort((a, b) => b.length - a.length)
    .find((k) => location.pathname === k || location.pathname.startsWith(`${k}/`)) || '/';
  const [pageTitle, pageSub] = titles[routeKey];
  const crumbs = routeKey.split('/').filter(Boolean);
  const nowLabel = new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date());

  return (
    <div className={`wb-layout ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((value) => !value)} />
      <main className="wb-content">
        {maintenanceMode ? (
          <div className="wb-global-maintenance-banner">
            Warning: Maintenance mode is ON - all merchants see the maintenance screen.
          </div>
        ) : null}
        <header className="wb-topbar">
          <div>
            <div className="wb-top-meta">{nowLabel}</div>
            <div className="wb-breadcrumbs">
              <span>Admin</span>
              {crumbs.map((crumb) => <span key={crumb}>{crumb.replace(/-/g, ' ')}</span>)}
            </div>
            <h1 className="wb-page-title">{pageTitle}</h1>
            <p className="wb-page-sub">{pageSub}</p>
          </div>
          <div className="wb-topbar-actions">
            <input
              className="wb-global-search"
              placeholder="Search merchants, invoices, audit logs"
              value={globalSearch}
              onChange={(event) => setGlobalSearch(event.target.value)}
            />
            <NotificationBell />
            <span className="wb-role-badge">{role}</span>
            <span className="wb-admin-chip">{auth.currentUser?.email || 'Admin'}</span>
            <button type="button" onClick={() => signOut(auth)}>Sign out</button>
          </div>
        </header>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/merchants" element={<MerchantList />} />
          <Route path="/merchants/:businessId" element={<MerchantDetail />} />
          <Route path="/promo-codes" element={<PromoCodes />} />
          <Route path="/operations" element={<Operations />} />
          <Route path="/operations/feature-flags" element={<Operations />} />
          <Route path="/operations/maintenance" element={<Operations />} />
          <Route path="/operations/broadcasts" element={<Operations />} />
          <Route path="/operations/task-queue" element={<TaskQueue />} />
          <Route path="/integrations" element={<Navigate to="/integrations/webhooks" replace />} />
          <Route path="/integrations/webhooks" element={<Webhooks />} />
          <Route path="/integrations/whatsapp-bot" element={<WhatsAppBot />} />
          <Route path="/integrations/api-keys" element={<ApiKeys />} />
          <Route path="/gst-master" element={<GSTMaster />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/admin-users" element={<AdminUsers />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <AuditToast />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route
          path="*"
          element={(
            <AdminGuard>
              <Shell />
            </AdminGuard>
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}
